import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api.ts";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const authUser = localStorage.getItem("authUser");

      if (authUser) {
        const parsedAuthUser = JSON.parse(authUser);
        if (parsedAuthUser?.token) {
          setIsAuthenticated(true);
          setUser(parsedAuthUser);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error("Error checking auth status:", error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    try {
      // Determine userType based on buyer/seller objects
      let userType = null;
      let userTypeId = null;
      if (userData?.buyer && userData.buyer !== null) {
        userType = 'buyer';
        userTypeId = userData.buyer.buyerTypeId;
      } else if (userData?.seller && userData.seller !== null) {
        userType = 'seller';
        userTypeId = userData?.seller?.sellerTypeId;
      }
      console.log(userData)
      // Create comprehensive user data object with all possible fields
      const authUserData = {
        token: token,
        // Basic user info
        email: userData.email,
        name: userData.name,
        username: userData.username,
        userType: userType,
        userTypeId: userTypeId,

        // Extended user profile fields
        mobileNo: userData.mobileNo || userData.phoneNumber,
        eid: userData.eid || userData.emiratesId,
        idExprDate: userData.idExprDate,
        bankName: userData.bankName,
        accountNumber: userData.accountNumber,
        accountType: userData.accountType,
        iban: userData.iban,
        city: userData.city,
        country: userData.country,

        // Timestamps
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt,

        // Any additional data
        ...userData,
      };

      const newUserData = { ...userData, token: token, userType: userType, userTypeId: userTypeId };

      // console.log("Processed authUserData:", authUserData);
      localStorage.setItem("authUser", JSON.stringify(newUserData));
      setIsAuthenticated(true);
      setUser(newUserData); // Use newUserData to match what's stored in localStorage
      return true;
    } catch (error) {
      console.error("Error during login:", error);
      return false;
    }
  };

  const logout = async () => {
    try {
      const logoout = await api.post(`/auth/logout`);

      localStorage.removeItem("authUser");
      setIsAuthenticated(false);
      setUser(null);

      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const updateUser = (userData) => {
    try {
      // Determine userType if buyer/seller data is being updated
      let userType = user?.userType; // Keep existing userType by default
      if (userData?.buyer !== undefined || userData?.seller !== undefined) {
        if (userData?.buyer && userData.buyer !== null) {
          userType = 'buyer';
        } else if (userData?.seller && userData.seller !== null) {
          userType = 'seller';
        } else if (userData?.buyer === null && userData?.seller === null) {
          userType = null;
        }
      }

      // Merge new userData with existing user data to preserve all fields
      const mergedUserData = {
        ...user, // Keep existing user data
        ...userData, // Override with new data
        token: userData.token || user?.token, // Preserve token if not provided
        userType: userType, // Set the determined userType
        updated_at: new Date().toISOString(), // Update timestamp
      };

      localStorage.setItem("authUser", JSON.stringify(mergedUserData));
      setUser(mergedUserData);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  // Utility function to update specific profile fields
  const updateProfile = (profileData) => {
    const profileFields = {
      mobileNo: profileData.mobileNo || profileData.phoneNumber,
      eid: profileData.eid,
      eid_expiry_date: profileData.eid_expiry_date,
      bank_name: profileData.bank_name,
      account_number: profileData.account_number,
      iban: profileData.iban,
      address: profileData.address,
      city: profileData.city,
      country: profileData.country,
      postal_code: profileData.postal_code,
      date_of_birth: profileData.date_of_birth,
      nationality: profileData.nationality,
      company_name: profileData.company_name,
      trade_license: profileData.trade_license,
      trade_license_expiry: profileData.trade_license_expiry,
    };

    // Remove undefined values
    const cleanedProfileData = Object.fromEntries(
      Object.entries(profileFields).filter(([_, value]) => value !== undefined)
    );

    updateUser(cleanedProfileData);
  };

  // Utility function to update verification status
  const updateVerificationStatus = (verificationData) => {
    const verificationFields = {
      is_verified: verificationData.is_verified,
      email_verified: verificationData.email_verified,
      phone_verified: verificationData.phone_verified,
      documents_verified: verificationData.documents_verified,
    };

    // Remove undefined values
    const cleanedVerificationData = Object.fromEntries(
      Object.entries(verificationFields).filter(
        ([_, value]) => value !== undefined
      )
    );

    updateUser(cleanedVerificationData);
  };

  // Utility function to get specific user data
  const getUserField = (fieldName) => {
    return user?.[fieldName] || null;
  };

  // Validation functions
  const validateUserData = (userData) => {
    const errors = {};

    // Email validation
    if (userData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      errors.email = "Invalid email format";
    }

    // Mobile number validation (UAE format)
    if (
      userData.mobileNo &&
      !/^(\+971|971|0)?[0-9]{9}$/.test(userData.mobileNo.replace(/\s/g, ""))
    ) {
      errors.mobileNo = "Invalid UAE mobile number format";
    }

    // Emirates ID validation (15 digits)
    if (userData.eid && !/^\d{15}$/.test(userData.eid.replace(/\s/g, ""))) {
      errors.eid = "Emirates ID must be 15 digits";
    }

    // IBAN validation (basic format check)
    if (
      userData.iban &&
      !/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/.test(userData.iban.replace(/\s/g, ""))
    ) {
      errors.iban = "Invalid IBAN format";
    }

    // Date validations
    if (
      userData.eid_expiry_date &&
      new Date(userData.eid_expiry_date) < new Date()
    ) {
      errors.eid_expiry_date = "Emirates ID has expired";
    }

    if (
      userData.trade_license_expiry &&
      new Date(userData.trade_license_expiry) < new Date()
    ) {
      errors.trade_license_expiry = "Trade license has expired";
    }

    return { isValid: Object.keys(errors).length === 0, errors };
  };

  // Utility function to check if user has complete profile
  const isProfileComplete = () => {
    if (!user) return false;

    const requiredFields = ["name", "email", "mobileNo"];
    const hasRequiredFields = requiredFields.every((field) => user[field]);

    return hasRequiredFields;
  };

  // Enhanced update functions with validation
  const updateUserWithValidation = (userData) => {
    const validation = validateUserData(userData);

    if (!validation.isValid) {
      console.error("Validation errors:", validation.errors);
      return { success: false, errors: validation.errors };
    }

    updateUser(userData);
    return { success: true, errors: {} };
  };

  const updateProfileWithValidation = (profileData) => {
    const validation = validateUserData(profileData);

    if (!validation.isValid) {
      console.error("Profile validation errors:", validation.errors);
      return { success: false, errors: validation.errors };
    }

    updateProfile(profileData);
    return { success: true, errors: {} };
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    updateUser,
    updateProfile,
    updateVerificationStatus,
    updateUserWithValidation,
    updateProfileWithValidation,
    validateUserData,
    getUserField,
    isProfileComplete,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
